import React from 'react';
import { Loader2, MapPin, Users, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const VEHICLE_NAMES = {
  shared: { es: 'Compartido', en: 'Shared' },
  standard: { es: 'Privado Estándar', en: 'Standard Private' },
  tahoe: { es: 'Privado Tahoe', en: 'Private Tahoe' },
  suburban: { es: 'Privado Suburban', en: 'Private Suburban' },
  limousine: { es: 'Limousine', en: 'Limousine' },
  group: { es: 'Grupal (9-30)', en: 'Group (9-30)' },
};

const QuoteResults = ({ quote, loading, onSelectVehicle, selectedKey }) => {
  const { lang } = useLanguage();

  if (loading) {
    return (
      <div className="mt-6 glass-card rounded-xl p-5 flex items-center justify-center gap-3 text-white/80">
        <Loader2 className="w-5 h-5 animate-spin text-[#ff6b1a]" />
        <span className="text-sm">{lang === 'es' ? 'Calculando precios...' : 'Calculating prices...'}</span>
      </div>
    );
  }

  if (!quote) return null;

  if (!quote.ok) {
    return (
      <div className="mt-6 glass-card rounded-xl p-5 text-center text-white/70 text-sm">
        <MapPin className="w-5 h-5 inline-block mr-2 text-[#ff6b1a]" />
        {quote.error}
      </div>
    );
  }

  const tt = quote.trip_type === 'round-trip'
    ? (lang === 'es' ? 'Ida y Vuelta' : 'Round Trip')
    : (lang === 'es' ? 'Solo Ida' : 'One Way');

  return (
    <div className="mt-6 glass-card rounded-xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4 pb-3 border-b border-white/5">
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="w-4 h-4 text-[#ff6b1a]" />
          <span className="text-white/60">{lang === 'es' ? 'Distancia:' : 'Distance:'}</span>
          <span className="text-white font-bold">{quote.distance_km} km</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Users className="w-4 h-4 text-[#ff6b1a]" />
          <span className="text-white/60">{lang === 'es' ? 'Pasajeros:' : 'Passengers:'}</span>
          <span className="text-white font-bold">{quote.passengers}</span>
        </div>
        <div className="text-sm">
          <span className="text-white/60">{lang === 'es' ? 'Tipo:' : 'Type:'}</span>
          <span className="text-white font-bold ml-1">{tt}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {quote.quotes.map((q) => {
          const name = VEHICLE_NAMES[q.vehicle_key]?.[lang] || q.vehicle_key;
          const isSelected = selectedKey === q.vehicle_key;
          return (
            <button
              key={q.vehicle_key}
              type="button"
              onClick={() => onSelectVehicle && onSelectVehicle(q)}
              className={`relative text-left rounded-lg p-4 transition-all border ${
                isSelected
                  ? 'bg-[#ff6b1a] border-[#ff6b1a] shadow-lg shadow-orange-500/30'
                  : 'bg-black/40 border-white/10 hover:border-[#ff6b1a]/50 hover:bg-black/60'
              }`}
            >
              {isSelected && (
                <div className="absolute top-2 right-2 w-5 h-5 bg-white rounded-full flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-[#ff6b1a]" strokeWidth={3} />
                </div>
              )}
              <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isSelected ? 'text-white/90' : 'text-[#ff6b1a]'}`}>
                {name}
              </div>
              <div className={`text-2xl font-black ${isSelected ? 'text-white' : 'text-white'}`}>
                ${q.price_total.toFixed(0)}
              </div>
              <div className={`text-xs mt-0.5 ${isSelected ? 'text-white/80' : 'text-white/50'}`}>
                {q.per_passenger ? (lang === 'es' ? 'precio total' : 'total price') : 'USD'}
                {q.per_passenger && ` · ~$${(q.price_total / quote.passengers).toFixed(0)}/${lang === 'es' ? 'pax' : 'pp'}`}
              </div>
              <div className={`text-[10px] mt-1 ${isSelected ? 'text-white/70' : 'text-white/40'}`}>
                {lang === 'es' ? 'Hasta' : 'Up to'} {q.max_pax} pax
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default QuoteResults;
